import { useState, useEffect, useRef } from 'react';
import { jobsApi, clientsApi, contractorsApi, quotesApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, Printer, Download, X, Send, CheckCircle2, XCircle } from 'lucide-react';
import { format } from 'date-fns';

const quoteStatuses = ['draft', 'sent', 'accepted', 'rejected', 'expired'];

// LHS Document Header Component with actual logo
const LHSDocumentHeader = ({ documentType, documentNumber, date, reference }) => (
  <div className="document-header rounded-t-lg">
    <div className="flex justify-between items-start">
      <div className="flex items-center gap-3">
        <img 
          src="https://customer-assets.emergentagent.com/job_park-case-tracker/artifacts/cww8tyf2_LHS%20LOGO%202%20.png" 
          alt="Leisure Home Solutions" 
          className="h-16 w-auto"
        />
      </div>
      <div className="text-right">
        <div className="text-sm opacity-80">{documentType} No:</div>
        <div className="document-number">{documentNumber}</div>
        <div className="text-sm mt-1">Date: {date}</div>
        {reference && <div className="text-sm">Ref: {reference}</div>}
      </div>
    </div>
  </div>
);

// Quote Preview Component
const QuotePreview = ({ quote, job, client, onClose }) => {
  const printRef = useRef();
  
  const handlePrint = () => {
    const content = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Quote ${quote.quote_number}</title>
          <style>
            body { font-family: 'Inter', -apple-system, sans-serif; margin: 0; padding: 20px; }
            .document-header { background: linear-gradient(135deg, #1a1a2e 0%, #1F7A78 70%, #0dbdad 100%); color: white; padding: 24px; border-radius: 8px 8px 0 0; }
            .document-logo { display: flex; align-items: center; gap: 12px; }
            .document-logo-icon { width: 48px; height: 48px; background: rgba(255,255,255,0.2); border-radius: 8px; display: flex; align-items: center; justify-content: center; }
            .lhs-logo-text { font-family: 'League Spartan', sans-serif; font-weight: 700; }
            .document-number { font-size: 1.5rem; font-weight: 600; }
            .section-title { color: #1F7A78; font-weight: 600; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.05em; border-bottom: 2px solid #0dbdad; padding-bottom: 4px; margin-bottom: 8px; }
            table { width: 100%; border-collapse: collapse; }
            th { background: #1F7A78; color: white; padding: 10px; text-align: left; font-size: 0.75rem; text-transform: uppercase; }
            td { padding: 10px; border-bottom: 1px solid #e5e7eb; }
            .total-row { background: #1F7A78; color: white; font-weight: bold; }
            .notes { background: #f9fafb; padding: 16px; border-radius: 8px; font-size: 0.875rem; }
          </style>
        </head>
        <body>${content.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <div className="sticky top-0 bg-white z-10 flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold">Quote Preview</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" /> Print
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div ref={printRef} className="quote-template">
          <LHSDocumentHeader 
            documentType="Quote"
            documentNumber={quote.quote_number || 'DRAFT'}
            date={quote.created_at ? format(new Date(quote.created_at), 'dd/MM/yyyy') : format(new Date(), 'dd/MM/yyyy')}
            reference={job?.job_number}
          />
          
          <div className="p-6 space-y-6">
            {/* Quotation To */}
            <div>
              <div className="document-section-title">Quotation To</div>
              <div className="text-sm">
                <p className="font-semibold">{client?.name || 'Client Name'}</p>
                {client?.address && <p>{client.address}</p>}
                {client?.email && <p>{client.email}</p>}
              </div>
            </div>

            {/* Quotation For */}
            <div>
              <div className="document-section-title">Quotation For Reinstatement Works</div>
              <div className="text-sm space-y-1">
                <p><strong>Site Address:</strong> {job?.property_address || quote.site_address || 'Property Address'}</p>
                {job?.property_postcode && <p><strong>Postcode:</strong> {job.property_postcode}</p>}
                {quote.claim_reference && <p><strong>Claim Ref:</strong> {quote.claim_reference}</p>}
                {quote.policy_number && <p><strong>Policy No:</strong> {quote.policy_number}</p>}
              </div>
            </div>

            {/* Scope of Works */}
            {quote.scope_of_works && (
              <div>
                <div className="document-section-title">Scope of Works</div>
                <div className="text-sm whitespace-pre-line">{quote.scope_of_works}</div>
              </div>
            )}

            {/* Line Items */}
            {quote.items && quote.items.length > 0 && (
              <div>
                <div className="document-section-title">Itemised Costs</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="bg-[#1F7A78] text-white p-2 text-left rounded-tl-md">Description</th>
                      <th className="bg-[#1F7A78] text-white p-2 text-right rounded-tr-md w-32">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote.items.map((item, idx) => (
                      <tr key={idx}>
                        <td className="p-2 border-b border-gray-200">{item.description}</td>
                        <td className="p-2 border-b border-gray-200 text-right">£{item.amount?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64">
                <div className="flex justify-between py-2 text-sm">
                  <span>Subtotal:</span>
                  <span>£{(quote.subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-3 font-bold text-white bg-[#1F7A78] rounded-md px-3">
                  <span>TOTAL:</span>
                  <span>£{(quote.total || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Notes & Conditions */}
            <div className="notes">
              <div className="document-section-title">Notes & Conditions</div>
              <ul className="text-sm space-y-1 list-disc list-inside text-gray-600">
                <li>All works carried out to current industry standards and manufacturer guidelines.</li>
                <li>Quotation valid for 30 days from the date above.</li>
                <li>Additional works identified once repairs commence may be subject to separate quotation.</li>
              </ul>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function QuotesPage() {
  const [quotes, setQuotes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [editingQuote, setEditingQuote] = useState(null);
  
  const [formData, setFormData] = useState({
    job_id: '',
    client_id: '',
    site_address: '',
    claim_reference: '',
    policy_number: '',
    scope_of_works: '',
    items: [{ description: '', amount: '' }],
    subtotal: 0,
    total: 0,
    status: 'draft',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsData, clientsData, contractorsData, quotesData] = await Promise.all([
        jobsApi.getAll(),
        clientsApi.getAll(),
        contractorsApi.getAll(),
        quotesApi.getAll(),
      ]);
      setJobs(jobsData);
      setClients(clientsData);
      setContractors(contractorsData);
      setQuotes(quotesData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      job_id: '', client_id: '', site_address: '', claim_reference: '', policy_number: '',
      scope_of_works: '', items: [{ description: '', amount: '' }], subtotal: 0, total: 0, status: 'draft',
    });
    setEditingQuote(null);
  };

  const openDialog = (quote = null) => {
    if (quote) {
      setEditingQuote(quote);
      // Map backend fields to form fields
      setFormData({
        job_id: quote.case_id || '',
        client_id: quote.client_id || '',
        site_address: quote.property_address || '',
        claim_reference: quote.reference || '',
        policy_number: quote.policy_number || '',
        scope_of_works: quote.scope_of_works || '',
        items: quote.items && quote.items.length > 0 ? quote.items : [{ description: '', amount: '' }],
        subtotal: quote.subtotal || 0,
        total: quote.total || 0,
        status: quote.status || 'draft',
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleJobSelect = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    if (job) {
      setFormData({
        ...formData,
        job_id: jobId,
        client_id: job.client_id || '',
        site_address: `${job.property_address}, ${job.property_postcode}`,
        claim_reference: job.claim_reference || '',
        policy_number: job.policy_number || '',
      });
    }
  };

  const addLineItem = () => {
    setFormData({ ...formData, items: [...formData.items, { description: '', amount: '' }] });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: field === 'amount' ? parseFloat(value) || 0 : value };
    
    const subtotal = newItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    const total = subtotal;
    
    setFormData({ ...formData, items: newItems, subtotal, total });
  };

  const removeLineItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    const subtotal = newItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    const total = subtotal;
    setFormData({ ...formData, items: newItems, subtotal, total });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const quoteData = {
        case_id: formData.job_id || null,
        client_id: formData.client_id || null,
        property_address: formData.site_address,
        reference: formData.claim_reference,
        scope_of_works: formData.scope_of_works,
        items: formData.items.filter(item => item.description || item.amount),
        subtotal: formData.subtotal,
        total: formData.total,
        status: formData.status,
      };
      
      if (editingQuote) {
        await quotesApi.update(editingQuote.id, quoteData);
        toast.success('Quote updated');
      } else {
        await quotesApi.create(quoteData);
        toast.success('Quote created');
      }
      
      // Refresh quotes list
      const updatedQuotes = await quotesApi.getAll();
      setQuotes(updatedQuotes);
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save quote:', error);
      toast.error('Failed to save quote');
    }
  };

  const handleDelete = async (quote) => {
    if (!window.confirm('Delete this quote?')) return;
    
    try {
      await quotesApi.delete(quote.id);
      const updatedQuotes = await quotesApi.getAll();
      setQuotes(updatedQuotes);
      toast.success('Quote deleted');
    } catch (error) {
      console.error('Failed to delete quote:', error);
      toast.error('Failed to delete quote');
    }
  };

  const handleSendQuote = async (quote) => {
    try {
      await quotesApi.send(quote.id);
      const updatedQuotes = await quotesApi.getAll();
      setQuotes(updatedQuotes);
      toast.success('Quote sent to client!');
    } catch (error) {
      console.error('Failed to send quote:', error);
      toast.error('Failed to send quote');
    }
  };

  const handleAcceptQuote = async (quote) => {
    try {
      await quotesApi.accept(quote.id);
      const updatedQuotes = await quotesApi.getAll();
      setQuotes(updatedQuotes);
      toast.success('Quote marked as accepted!');
    } catch (error) {
      console.error('Failed to accept quote:', error);
      toast.error('Failed to accept quote');
    }
  };

  const handleRejectQuote = async (quote) => {
    if (!window.confirm('Mark this quote as rejected?')) return;
    try {
      await quotesApi.reject(quote.id);
      const updatedQuotes = await quotesApi.getAll();
      setQuotes(updatedQuotes);
      toast.info('Quote marked as rejected');
    } catch (error) {
      console.error('Failed to reject quote:', error);
      toast.error('Failed to reject quote');
    }
  };

  const handlePreview = (quote) => {
    setSelectedQuote(quote);
    setIsPreviewOpen(true);
  };

  const getJobInfo = (jobId) => jobs.find(j => j.id === jobId);
  const getClientInfo = (clientId) => clients.find(c => c.id === clientId);

  const columns = [
    { key: 'quote_number', label: 'Quote #' },
    { key: 'case_id', label: 'Job', render: (val) => getJobInfo(val)?.job_number || '-' },
    { key: 'client_id', label: 'Client', render: (val) => getClientInfo(val)?.name || '-' },
    { key: 'property_address', label: 'Site', render: (val) => val ? (val.substring(0, 30) + (val.length > 30 ? '...' : '')) : '-' },
    { key: 'total', label: 'Total', render: (val) => `£${(val || 0).toFixed(2)}` },
    { key: 'status', label: 'Status', render: (val) => <StatusBadge status={val} /> },
    { key: 'created_at', label: 'Date', render: (val) => val ? format(new Date(val), 'dd/MM/yyyy') : '-' },
  ];

  // Dynamic actions based on quote status
  const getQuoteActions = (quote) => {
    const actions = [
      { label: 'Preview', icon: Eye, onClick: handlePreview },
      { label: 'Edit', icon: Pencil, onClick: openDialog },
    ];

    // Add status-specific actions
    if (quote.status === 'draft') {
      actions.push({ label: 'Send to Client', icon: Send, onClick: handleSendQuote, className: 'text-blue-600' });
    }
    
    if (quote.status === 'sent') {
      actions.push({ label: 'Mark Accepted', icon: CheckCircle2, onClick: handleAcceptQuote, className: 'text-green-600' });
      actions.push({ label: 'Mark Rejected', icon: XCircle, onClick: handleRejectQuote, className: 'text-red-600' });
    }

    actions.push({ label: 'Delete', icon: Trash2, onClick: handleDelete, destructive: true });
    
    return actions;
  };

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Quotes</h1>
          <p className="text-sm text-gray-500 mt-1">Create and manage quotations</p>
        </div>
        <Button onClick={() => openDialog()} className="btn-primary" data-testid="add-quote-btn">
          <Plus className="h-4 w-4 mr-2" /> New Quote
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={quotes}
        actions={getQuoteActions}
        isLoading={isLoading}
        emptyMessage="No quotes found. Create your first quote!"
      />

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle>{editingQuote ? 'Edit Quote' : 'Create New Quote'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Job Selection */}
            <div className="space-y-2">
              <Label>Link to Job</Label>
              <Select value={formData.job_id} onValueChange={handleJobSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Select job to auto-fill details" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No linked job</SelectItem>
                  {jobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>{j.job_number} - {j.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client</Label>
                <Select value={formData.client_id} onValueChange={(val) => setFormData({ ...formData, client_id: val })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {quoteStatuses.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Site Address *</Label>
              <Input
                value={formData.site_address}
                onChange={(e) => setFormData({ ...formData, site_address: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Claim Reference</Label>
                <Input
                  value={formData.claim_reference}
                  onChange={(e) => setFormData({ ...formData, claim_reference: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Policy Number</Label>
                <Input
                  value={formData.policy_number}
                  onChange={(e) => setFormData({ ...formData, policy_number: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Scope of Works</Label>
              <Textarea
                value={formData.scope_of_works}
                onChange={(e) => setFormData({ ...formData, scope_of_works: e.target.value })}
                rows={4}
                placeholder="Describe the works to be carried out..."
              />
            </div>

            {/* Line Items */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Cost Breakdown</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                  <Plus className="h-3 w-3 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {formData.items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-start">
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Amount"
                      value={item.amount || ''}
                      onChange={(e) => updateLineItem(idx, 'amount', e.target.value)}
                      className="w-32"
                    />
                    {formData.items.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeLineItem(idx)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between font-bold text-lg">
                <span>Total:</span>
                <span className="text-[#1F7A78]">£{formData.total.toFixed(2)}</span>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary">
                {editingQuote ? 'Update' : 'Create'} Quote
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      {isPreviewOpen && selectedQuote && (
        <QuotePreview 
          quote={selectedQuote} 
          job={getJobInfo(selectedQuote.job_id)}
          client={getClientInfo(selectedQuote.client_id)}
          onClose={() => setIsPreviewOpen(false)} 
        />
      )}
    </div>
  );
}
